<template>
  <div
    v-if="appointment"
    class="appointment-treatment-selector mx-auto w-full max-w-screen-md flex flex-col"
  >
    <div class="flex justify-between items-center">
      <!-- // API TODO: Initials should be doctors initials for overall appt -->
      <PatientListItem
        :initialsItem="appointment.doctor"
        :initialsItemDoctors="appointment.doctors"
        :image-url="appointment.patient.avatar"
        :title="appointment.patient.name"
        :timestamp="appointment.starts_at"
        no-padding
      />
      <div class="d-flex justify-start gap-2 flex-row-reverse w-4/12">
        <v-btn
          class="py-2 px-2"
          color="gray-50"
          icon="mdi-close"
          density="comfortable"
          variant="text"
          @click="closeModal"
        />

        <v-btn color="primary" @click="showConfirmation"
               v-if="canCheckOut && interactWithBoard">
          CHECK OUT
        </v-btn>
        <v-btn color="primary" @click="showManualWarningFor(manualCheckIn)"
               v-else-if="candidateToManual && interactWithBoard">
            MANUAL CHECK-IN
        </v-btn>
        <v-btn color="primary" @click="showManualWarningFor(returnToUpcoming)"
               v-else-if="canReturnBackToUpcoming && interactWithBoard">
            RETURN TO UPCOMING
        </v-btn>
        <v-btn color="primary" @click="checkInBack"
               v-else-if="canReturnToArrived && interactWithBoard">
            CHECK BACK IN
        </v-btn>
      </div>
    </div>

    <v-sheet class="bg-white rounded">
      <v-tabs v-model="activeTab" color="primary" hide-slider>
        <v-tab value="treatments">Treatments</v-tab>
        <v-tab value="appointments">Appointments</v-tab>
        <v-tab value="notes">Notes</v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-tabs-window v-model="activeTab">
        <v-tabs-window-item value="treatments" style="height: 650px; overflow-y: scroll;">
          <TreatmentsTabContent
            :treatments="treatments"
            :treatments-doctors="treatmentsDoctors"
            :appt-treatment-list="apptTreatmentList"
            :completed-treatments="completedTreatments"
            :interact-with-board="interactWithBoard"
            :today-and-not-completed="todayAndNotCompleted"
            :show-draggable="showDraggable"
            :can-apply-last-appointment="canApplyLastAppointment"
            :can-clear-appointment="canClearAppointment"
            :is-processing="isProcessing"
            :telehealth-room="telehealthRoom"
            :link-copied="telehealthLinkCopied"
            @card-default-click="onCardDefaultClicked"
            @select-treatment="handleSelectTreatment"
            @remove-treatment="onTreatmentRemoveClicked"
            @apply-last-appointment="applyLastAppointment"
            @clear-appointment="handleClearAppointment"
            @copy-link="copyTelehealthLink"
            @drag-start="dragging = true"
            @drag-end="dragging = false"
          />
        </v-tabs-window-item>
        <v-tabs-window-item value="appointments" style="height: 650px; overflow-y: scroll;">
          <AppointmentsList />
        </v-tabs-window-item>
        <v-tabs-window-item value="notes" style="height: 650px; overflow-y: scroll;">
          <div class="overflow-y-scroll flex-grow pa-2 pt-4">
            <div class="notes-module-body">
              <div v-if="!!notes">
                <TreatmentNotesCard
                  v-for="(noteArray, treatment) in notes"
                  :key="`${treatment}-notes`"
                  :notes="noteArray"
                  :appointment-id="appointment.uuid"
                  :patient-id="appointment.patient.uuid"
                  :patient-name="appointment.patient.name"
                  :treatment-id="String(treatment)"
                  :hide-patient="true"
                  is-appointment-dialog
                  @refresh-notes="refreshNotes"
                  @set-allowed-close="setAllowedToClose"
                />
              </div>

              <div class="text-center text-button mb-4 pt-2" >
                  <AddNewNote
                    :appointment-id="appointment.uuid"
                    :patient-id="appointment.patient.uuid"
                    :hide-header-text="!!notes"
                    @refresh-notes="refreshNotes"
                    @set-allowed-close="setAllowedToClose"
                  />
              </div>
            </div>
          </div>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-sheet>

    <WarningConfirmDialog
      v-model="showManualWarning"
      message="This is an emergency-only action. Use only when DrChrono sync is down."
      @confirm="onManualWarningConfirmed"
    />
  </div>
</template>

<script lang="ts">
import { boolean } from 'boolean';
import { defineComponent } from 'vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import equal from 'fast-deep-equal/es6';
import randomColor from 'randomcolor';
import { mdiClose } from '@mdi/js';
import { orderBy, groupBy } from 'lodash-es';

import PatientListItem from '@/components/basic/PatientListItem.vue';
import TreatmentNotesCard from '@/components/cards/TreatmentNotesCard.vue';
import AddNewNote from '@/components/cards/AddNewNote.vue';
import AppointmentsList from '@/components/modules/AppointmentsList.vue';
import TreatmentsTabContent from '@/components/modules/TreatmentsTabContent.vue';
import WarningConfirmDialog from '@/components/modules/WarningConfirmDialog.vue';

declare interface Treatment {
  uuid: string;
  name: string;
}

declare interface TreatmentDoctor {
  uuid: string;
  hasDoctors: boolean;
}

declare interface BaseData {
  dragging: boolean;
  isProcessing: boolean;
  showDraggable: boolean;
  closeIcon: any;
  treatmentsDoctors: TreatmentDoctor[];
  apptTreatmentList: any[];
  openAppointment: any;
  activeTab: string;
  notes: any[];
  interactWithBoard: boolean;
  showManualWarning: boolean;
  manualWarningAction: (() => void) | null;
  telehealthLinkCopied: boolean;
}

export default defineComponent({
  name: 'AppointmentTreatmentSelector',
  components: {
    AddNewNote,
    TreatmentNotesCard,
    PatientListItem,
    AppointmentsList,
    TreatmentsTabContent,
    WarningConfirmDialog,
  },
  data(): BaseData {
    return {
      dragging: false,
      isProcessing: false,
      telehealthLinkCopied: false,
      closeIcon: mdiClose,
      showDraggable: true,
      treatmentsDoctors: [],
      apptTreatmentList: [],
      openAppointment: undefined,
      activeTab: 'treatments',
      notes: [],
      interactWithBoard: false,
      showManualWarning: false,
      manualWarningAction: null as (() => void) | null,
    };
  },
  computed: {
    ...mapState({
      // @ts-ignore
      appointment: (state) => state.appointment,
      // @ts-ignore
      confirmation: (state) => state.confirmation,
      // @ts-ignore
      doctors: (state) => state.doctors,
      // @ts-ignore
      lastAppointment: (state) => state.lastAppointment,
      // @ts-ignore
      dashboard: (state) => state.auth.dashboard,
      treatments({ auth: { dashboard }, rooms, treatments }) {
        const { uuid } = dashboard?.rooms?.find(({ appointments: a }) =>
          a?.find((x) => x?.uuid === this.appointment?.uuid),
        ) ?? {};
        if (!!this.appointment && !['processing', 'paused'].includes(this.appointment.status) ) {
          return treatments.filter(({is_enabled}) => is_enabled === 'yes');
        }
        return rooms.find((x) => x?.uuid === uuid)?.treatments.filter(({is_enabled}) => is_enabled === 'yes') ?? [];
      },
      appointmentHasCompletedTreatment() {
        const completedTreatments = this.appointment.treatments.filter((t) => t.status === 'completed');
        return completedTreatments?.length > 0;
      },
      room({ auth: { dashboard }, rooms }) {
        const { uuid } = dashboard?.rooms?.find(({ appointments: a }) =>
          a?.find((x) => x?.uuid === this.appointment?.uuid),
        ) ?? {};
        return rooms.find((x) => x?.uuid === uuid);
      },
      user: (state: any) => state.auth.user,
    }),
    telehealthRoom() {
      return this.appointment?.doctors?.find((d) => d.telehealth?.url)?.telehealth ?? null;
    },
    canApplyLastAppointment() {
      return !this.appointment?.treatments?.length && this.lastAppointment?.uuid;
    },
    canClearAppointment() {
      return !!(
        this.appointment &&
        this.appointment.treatments &&
        this.appointment.treatments.length &&
        !this.appointmentHasCompletedTreatment
      );
    },
    completedTreatments() {
      const completed = this.appointment.treatments.filter((t) => t.ended_at);
      return orderBy(completed, 'ended_at');
    },
    candidateToManual() {
      return this.appointment.status === 'pending';
    },
    todayAndNotCompleted() {
      const isToday = this.$dayjs(this.appointment.starts_at).isToday();
      const onDashboard = ['processing', 'paused'].includes(this.appointment.status);
      const notCompleted = !['completed'].includes(this.appointment.status);

      return isToday && (onDashboard || notCompleted);
    },
    canCheckOut() {
      return !['waiting', 'completed', 'cancelled', 'pending'].includes(this.appointment.status) && !this.appointment.ended_at;
    },
    canReturnToArrived() {
      return ['completed'].includes(this.appointment.status) && this.appointment.ended_at;
    },
    canReturnBackToUpcoming() {
      return this.appointment.status === 'waiting' && this.appointment.manual;
    },
    mainBtnText() {
      return ['processing', 'paused'].includes(this.appointment.status)
        ? 'CHECK OUT'
        : 'START';
    },
  },
  watch: {
    appointment({ patient: { uuid = null } = {}} = {}) {
      console.log('triggered appointment', this.appointment.patient.name);
      this.copyTreatmentsForDisplay();

      if (this.openAppointment !== this.appointment.uuid) {
        const payload = {
          appointmentId: this.appointment.uuid,
        };
        this.openAppointment = this.appointment.uuid;
        this.$store.dispatch('LOAD_SELECTED_APPOINTMENT', payload);
      }

      this.loadPatientAppointmentsNotes();
      this.setApptTreatmentList();
    },
    confirmation({ uuid, wasConfirmed }) {
      if (uuid === this.appointment.uuid && wasConfirmed !== null) {
        this.$store.dispatch('RESET_CONFIRMATION');
        if (wasConfirmed) { this.handleCheckOutAppointment(); }
      }
    },
    doctors() {
      this.treatmentsDoctors = this.treatments.map((t) => ({uuid: t.uuid, hasDoctors: false}));
      this.treatmentsDoctors.forEach((t) => this.setTreatmentsDoctors(t));
    },
    treatments() {
      this.treatmentsDoctors = this.treatments.map((t) => ({uuid: t.uuid, hasDoctors: false}));
      this.treatmentsDoctors.forEach((t) => this.setTreatmentsDoctors(t));
    },
    apptTreatmentList: 'sortTreatments',
    activeTab: 'getActiveTabInfo',
  },
  async mounted() {

    this.interactWithBoard = await this.$store.dispatch('ableUserTo', 'interact with board');

    await this.LOAD_ROOMS();

    // treatmentsDoctors should be replaced with a computed property
    this.treatmentsDoctors = this.treatments.map((t: any) => ({
      hasDoctors: false,
      uuid: t.uuid,
    }));
    this.treatmentsDoctors.forEach((t: any) => this.setTreatmentsDoctors(t));

    if (this.openAppointment !== this.appointment.uuid) {
      const payload = {
        appointmentId: this.appointment.uuid,
      };
      this.openAppointment = this.appointment.uuid;
      this.$store.dispatch('LOAD_SELECTED_APPOINTMENT', payload);
    }
  },
  methods: {
    async copyTelehealthLink(): Promise<void> {
      if (!this.telehealthRoom?.url) return;

      try {
        await navigator.clipboard.writeText(this.telehealthRoom.url);
        this.telehealthLinkCopied = true;
        setTimeout(() => { this.telehealthLinkCopied = false; }, 2000);
      } catch (e) {
        this.$eventBus.emit('emit-error', { state: true, text: 'Could not copy the link' });
      }
    },
    ...mapActions([
      'ADD_APPOINTMENT_TREATMENT',
      'ASK_FOR_CONFIRMATION',
      'DELETE_APPOINTMENT_TREATMENT',
      'DELETE_APPOINTMENT_ALL_TREATMENTS',
      'LOAD_DASHBOARD',
      'LOAD_ROOMS',
      'RESET_CONFIRMATION',
      'selectTreatment',
      'UPDATE_APPOINTMENT_STATUS',
      'HANDLE_COMPLETE_TREATMENT',
      'UPDATE_APPT_TREATMENT_ORDER',
      'LOAD_PATIENT_NOTES',
      'LOAD_PATIENT_APPOINTMENTS',
    ]),
    ...mapMutations([
      'toggleAppointment',
      'updateStopLastAppointmentsOnMount',
    ]),
    flagStyle(string) {
      const substr = 'MED FLAG';
      let style = { backgroundColor: '#FFDEDE', color: '#AB0000', fontWeight: '500'};
      if (!string.includes(substr)) {
        style = { backgroundColor: '#EBE7E7', color: '#000', fontWeight: '500'};
      }

      return style;
    },
    closeModal() {
      this.$emit('close-dialog');
    },
    setAllowedToClose(value: boolean) {
      this.$emit('set-allowed-close', value);
    },
    copyTreatmentsForDisplay() {
      this.apptTreatmentList = JSON.parse(JSON.stringify(this.appointment?.treatments));
    },
    async sortTreatments() {
      const { uuid: appointmentId } = this.appointment;
      const draggedList = this.getOrder(this.apptTreatmentList);
      const oldOrderList = this.getOrder(this.appointment.treatments);

      if (!equal(draggedList, oldOrderList)) {

        const success = await this.UPDATE_APPT_TREATMENT_ORDER({
          appointmentId,
          params: {
            treatmentsUuidAr: draggedList,
          },
        });

        if (success) {
          this.$eventBus.emit('emit-success', { state: true, text: 'Order update was succeed!' });
          this.dashboard
              .rooms.find(({ appointments: a }) =>
              a?.find((x) => x?.uuid === this.appointment?.uuid),
          )
              .appointments[0].treatments = this.apptTreatmentList;
        } else {
          this.$eventBus.emit('emit-error', { state: true, text: 'Order update was Failed!' });
        }
      }

    },
    async getActiveTabInfo() {

      if (this.activeTab === 'notes') {
        const data = await this.LOAD_PATIENT_NOTES();

        if (data) {
          // this.notes = data.patient.notes;
          const notes = groupBy(data.patient.notes, 'treatment.uuid');
          this.notes = notes ?? [];
        }
      } else if (this.activeTab === 'appointments') {
        const data = await this.LOAD_PATIENT_APPOINTMENTS();
      }
    },
    async loadPatientAppointmentsNotes() {
      if (this.activeTab === 'notes') {
        const data = await this.LOAD_PATIENT_NOTES();

        if (data) {
          // this.notes = data.patient.notes;
          const notes = groupBy(data.patient.notes, 'treatment.uuid');
          this.notes = notes ?? [];
        }
      }
    },
    async refreshNotes() {
      const data = await this.LOAD_PATIENT_NOTES();

      if (data) {
        this.notes = groupBy(data.patient.notes, 'treatment.uuid');
      }
    },
    setApptTreatmentList() {

      if (!equal(this.apptTreatmentList, this.appointment.treatments)) {
        this.apptTreatmentList = Object.assign([], this.appointment?.treatments);
      }
    },
    getOrder(treatments) {
      const treatmentsUuidAr = [];

      if (treatments.length === 0) { return treatmentsUuidAr; }

      treatments.map((t) => {
        treatmentsUuidAr.push(t.uuid);
      });

      return treatmentsUuidAr;
    },
    async applyLastAppointment() {
      this.isProcessing = true;
      const payload = {
        appointmentId: this.appointment.uuid,
        params: {
          prevAppointmentUuid: this.lastAppointment?.uuid,
        },
      };

      const success = await this.$store.dispatch('LOAD_LAST_APPOINTMENT_TREATMENTS', payload);

      if (success) {
        this.$eventBus.emit('emit-success', { state: true, text: 'Last appointment treatments loaded' });
      } else {
        this.$eventBus.emit('emit-error', {state: true, text: 'Couldn\'t load last appointment treatments'});
      }

      this.isProcessing = false;
    },
    async deleteTreatment(uuid: string) {
      const { uuid: appointmentId } = this.appointment;
      this.apptTreatmentList.filter( (t) => t.uuid !== uuid );
      await this.DELETE_APPOINTMENT_TREATMENT({ appointmentId, params: {
        treatment: uuid,
      }});
    },
    async handleCheckOutAppointment() {
      const {
        $store: { state: { auth: { user: { uuid: user }}}},
        appointment: {
          status: statusBeforeCheckout,
          uuid: appointmentId,
        },
      } = this;
      const params = {
        status: {
          waiting: 'processing',
          paused: 'completed',
          processing: 'completed',
        }[statusBeforeCheckout],
        user,
        localInfo: {
          appointmentId,
        },
      };
      await this.UPDATE_APPOINTMENT_STATUS({ appointmentId, params });
      this.toggleAppointment({ bool: false });
    },
    async handleClearAppointment() {
      const { treatments } = this.appointment;
      if (!treatments?.length) { return; }
      const hasActiveTreatment = treatments.find((t) => ['paused', 'processing'].includes(t.status));
      let message = 'Are you sure to delete all treatments?';

      if (hasActiveTreatment) {
        console.log('hasActiveTreatment', hasActiveTreatment);
        message += ' You have active treatment!';
      }

      const result = confirm(message);
      console.log('result', result);

      if (result) {
        this.showDraggable = false;
        const { uuid: appointmentId } = this.appointment;
        this.apptTreatmentList.filter((t) => false);
        await this.DELETE_APPOINTMENT_ALL_TREATMENTS({ appointmentId });
        setTimeout(() => this.showDraggable = true, 100);
      }
    },
    handleSelectTreatment(treatment: Treatment) {
      this.selectTreatment({ treatment });
      this.$emit('open-treatment-step');
    },
    async onCardDefaultClicked(payload) {
      if (!payload?.uuid) { return; }
      const treatment = payload.uuid;

      const {
        $store: { state: { auth: { user: { uuid: user }}}},
        appointment: {
          uuid: appointmentId,
        },
      } = this;
      const room = this.room?.uuid ?? null;

      const qualifiedDoctor = this.doctors
        .filter(({ is_enabled: x }) => boolean(x))
        .find((d) => d.treatments.find((t) => t.uuid === treatment));

      const doctorUuid = this.appointment?.doctor?.uuid ?? qualifiedDoctor?.uuid;
      const matchedTreatment = qualifiedDoctor?.treatments.find((t) => t.uuid === treatment);

      if (!doctorUuid || !matchedTreatment) {
        this.$eventBus.emit('emit-error', {
          state: true,
          text: 'No enabled practitioner is qualified for this treatment.',
        });
        return;
      }

      const requestPayload = { appointmentId, params: {
          doctor: doctorUuid,
          duration: matchedTreatment.duration * 60,
          room,
          treatment,
          user,
        }};
      await this.ADD_APPOINTMENT_TREATMENT(requestPayload);
      this.setApptTreatmentList();
    },
    async onTreatmentRemoveClicked(uuid: string) {
      await this.deleteTreatment(uuid);
    },
    setTreatmentsDoctors(treatment) {
      const filteredDoctors = this.doctors.filter((dr) => {
        return dr.treatments.find((t) => t.uuid === treatment.uuid);
      });
      if (filteredDoctors.length > 0) {
        const updatedTreatmentsDoctors = [...this.treatmentsDoctors];
        updatedTreatmentsDoctors[updatedTreatmentsDoctors.findIndex((t) => t.uuid === treatment.uuid)].hasDoctors = true;
        this.treatmentsDoctors = updatedTreatmentsDoctors;
      }
    },
    showConfirmation() {
      this.ASK_FOR_CONFIRMATION({
        message: 'This appointment will be removed from the board after checkout',
        uuid: this.appointment.uuid,
      });
    },
    doctorStyle(doctor) {
      const backgroundColor = doctor?.color ?? randomColor();
      return {
        backgroundColor,
        color: this.$filters.foregroundColor(backgroundColor),
      };
    },
    showManualWarningFor(action: () => void) {
      this.manualWarningAction = action;
      this.showManualWarning = true;
    },
    onManualWarningConfirmed() {
      if (this.manualWarningAction) {
        this.manualWarningAction();
        this.manualWarningAction = null;
      }
    },
    async manualCheckIn() {
      console.log('MANUAL CHECK-IN ACTION');
      const {
        $store: { state: { auth: { user: { uuid: user }}}},
        appointment: {
          uuid: appointmentId,
        },
      } = this;
      const params = {
        status: 'manual',
        user,
        localInfo: {
          appointmentId,
        },
      };

      await this.UPDATE_APPOINTMENT_STATUS({ appointmentId, params });
    },
    async returnToUpcoming() {
      console.log('MANUAL APPOINTMENT RETURNING TO UPCOMING');
      const {
        $store: { state: { auth: { user: { uuid: user }}}},
        appointment: {
          uuid: appointmentId,
        },
      } = this;
      const params = {
        status: 'upcoming',
        user,
        localInfo: {
          appointmentId,
        },
      };
      await this.UPDATE_APPOINTMENT_STATUS({ appointmentId, params });
    },
    async checkInBack() {
      console.log('Appointment returning back from checked out column');
      const {
        $store: { state: { auth: { user: { uuid: user }}}},
        appointment: {
          uuid: appointmentId,
          status,
        },
      } = this;
      const params = {
        status: 'waiting',
        user,
        localInfo: {
          appointmentId,
          status,
        },
      };
      await this.UPDATE_APPOINTMENT_STATUS({ appointmentId, params });
    },
  },
});
</script>

<style scoped>
/* Styles moved to TreatmentsTabContent.vue */
</style>
